import { useCallback, useRef, useState } from 'react';

export type Status = 'idle' | 'pending' | 'success' | 'error';
type Unpromisify<T> = T extends Promise<infer R> ? R : T

function useRequest<T extends (...args: never[]) => Promise<any>>(asyncFunction: T) {
  type Params = Parameters<T>;
  type Ret = Unpromisify<ReturnType<T>>;
  
  const [status, setStatus] = useState<Status>('idle');
  const [result, setResult] = useState<Ret>();
  const [error, setError] = useState<any>();

  const execute = useCallback(async (...args: Params) => {
    setResult(undefined);
    setError(undefined);
    setStatus('pending');
    
    try {
      const response = await asyncFunction(...args);
      setResult(response);
      setStatus('success');
    }
    catch (error) {
      setError(error);
      setStatus('error');
    }
  }, [asyncFunction]);

  const hook = { execute, status, result, error };
  const hookRef = useRef(hook);
  Object.assign(hookRef.current, hook);

  return [hookRef.current];
}

export default useRequest;
